import React, { useMemo } from 'react';
import { IActionCriteria } from './DocumentProvider';
import {
  FilterOperation,
  IAbstractContact,
  IAbstractDocumentDefault,
  ICustomer,
  ICustomerContact,
  ICustomerDocumentDefault,
  IName,
  IOrderConfig,
  IReportParametersInput,
  IReportTemplate,
  ISalesOrder,
  ISupplier,
  ISupplierDocumentDefault,
  JobType,
  Maybe,
  ReportOutputType,
} from '../../graphql/schema/p4types';
import { getDefaultOutputType } from '../../utils/getDefaultOutputType';
import { emailFromEntity, IEntityEmail } from '../../utils/EmailUtils';
import {
  DOCUMENT_SUMMARY,
  PRINT_INDIVIDUAL_PREFERENCE_COOKIE,
} from '../../views/document/AcknowledgementDocumentCarouselCard';
import { Badge, makeStyles } from '@fluentui/react-components';
import { UseFormGetValues } from 'react-hook-form/dist/types/form';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useSelector } from 'react-redux';
import { EntityType } from '../../@types/entityTypes';
import { IReduxStore } from '../../@types/reduxTypes';
import getIn from '../connectedForm/utils/getIn';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';

type IAbstractCommonDocumentDefault = ICustomerDocumentDefault | ISupplierDocumentDefault;

export type IDocumentValues = {
  overrideOutput: boolean;
  outputType?: Maybe<ReportOutputType>;
  overrideTemplate: boolean;
  template?: Maybe<IReportTemplate>;
  email: {
    from?: IEntityEmail;
    to: Array<IEntityEmail>;
    cc: Array<IEntityEmail>;
    subject?: string;
    message?: string;
  };
  printIndividually: boolean;
  printContiguous: boolean;
  showSummary: boolean;

  /**
   * System Printer ID
   */
  printerId?: number;
  recipientForEmail: ICustomer | ISupplier;

  /**
   * Delivery Note Only. TODO - Also conformity certificate?
   */
  resetToStart?: boolean;
};

export const useActionFilters = (actionCriteria: IActionCriteria) => {
  return useMemo(() => {
    if (actionCriteria.include) {
      //If we have inclusions, we don't care about the action criteria filters, just include the specified records.
      return {
        filterOperation: FilterOperation.In,
        filterParameters: {
          field: 'id',
          value: actionCriteria.actionEntities.map((value) => `${value.id}`).join(','),
        },
      };
    }
    //For exclusions, take the specified filters and add a notIn.
    return [
      ...actionCriteria.filters,
      {
        filterOperation: FilterOperation.NotIn,
        filterParameters: {
          field: 'id',
          value: actionCriteria.actionEntities.map((value) => `${value.id}`).join(','),
        },
      },
    ];
  }, [actionCriteria.actionEntities, actionCriteria.filters, actionCriteria.include]);
};

export const useDocumentCarouselDefaults = ({
  entity,
  recipient,
  recipientDocumentDefault,
  commonDocumentDefault,
  singleRecipient,
  cookies,
  allowEntityEmail = true,
  salesOrderConfig,
}: {
  entity: EntityType;
  recipient: ICustomer | ISupplier;
  recipientDocumentDefault?: IAbstractCommonDocumentDefault;
  commonDocumentDefault: IAbstractDocumentDefault;
  singleRecipient: boolean;
  cookies: { [DOCUMENT_SUMMARY]: string; [PRINT_INDIVIDUAL_PREFERENCE_COOKIE]: string };
  allowEntityEmail: boolean; //Include the "recipients" email address - I.e. 'customer.email' rather than requiring a contact email.
  salesOrderConfig?: Maybe<IOrderConfig>;
}): IDocumentValues => {
  const systemUser = useSelector((store: IReduxStore) => store.app.user);
  return useMemo<IDocumentValues>(() => {
    let recipientForEmail = recipient;
    let documentDefault = recipientDocumentDefault;
    const receiveFlag: string | undefined = entity.configuration?.receiveFlag;
    const entityBaseConfig = entity.configuration?.defaultConfig;
    const recipientAllContacts = recipient?.contacts ?? [];
    let recipientOtherContacts = receiveFlag
      ? recipientAllContacts.filter((contact) => contact![receiveFlag]) ?? []
      : [];

    if (
      salesOrderConfig?.documentPreferencesFromHeadOffice &&
      !recipientDocumentDefault?.layout &&
      recipientOtherContacts.length === 0 &&
      entity.configuration?.customerField &&
      recipient?.headOffice
    ) {
      const headOfficeDocumentDefault = getIn(recipient.headOffice, entity.configuration?.customerField);
      if (headOfficeDocumentDefault) {
        recipientForEmail = recipient.headOffice;
        documentDefault = headOfficeDocumentDefault;
        recipientOtherContacts = getIn(recipient.headOffice, 'contacts')
          ? getIn(recipient.headOffice, 'contacts').filter((contact: ICustomerContact) => {
              return contact[receiveFlag as keyof typeof contact];
            })
          : [];
      }
    }

    const showSummary = cookies[DOCUMENT_SUMMARY] === 'true'; // Note, cookie stored as text
    const overrideTemplate = singleRecipient;
    const template: Maybe<IReportTemplate> = documentDefault?.layout ?? commonDocumentDefault?.layout;
    const overrideOutput = singleRecipient;
    const outputType: Maybe<ReportOutputType> =
      documentDefault?.output ??
      commonDocumentDefault?.output ??
      entityBaseConfig?.output ??
      getDefaultOutputType(template?.reportTemplateFormat).value;
    const printerId = documentDefault?.systemPrinterId ?? commonDocumentDefault?.systemPrinterId;
    const emailConfig = {
      to: singleRecipient
        ? emailFromEntity(recipientForEmail, undefined, recipientOtherContacts as Array<IAbstractContact>, {
            allowEntityEmail,
            showFlagOnDefault: true,
          }) ?? []
        : [],
      cc: [],
      from:
        systemUser && commonDocumentDefault?.usersEmailAsFromAddress
          ? { email: systemUser.email, name: systemUser.name, id: 'user' }
          : { email: commonDocumentDefault?.fromAddress, name: 'Default', id: 'default' },
      subject: documentDefault?.subject ?? commonDocumentDefault?.subject,
      message: commonDocumentDefault?.body ?? '',
    };

    const printIndividually = false;
    const printContiguous = cookies[PRINT_INDIVIDUAL_PREFERENCE_COOKIE] === 'true';

    const defaultValues: IDocumentValues = {
      overrideTemplate,
      template,
      overrideOutput,
      outputType,
      printerId,
      showSummary,
      email: emailConfig,
      printIndividually,
      printContiguous,
      recipientForEmail,
    };
    return defaultValues;
  }, [
    entity,
    recipient,
    recipientDocumentDefault,
    commonDocumentDefault,
    singleRecipient,
    cookies,
    allowEntityEmail,
    systemUser,
    salesOrderConfig,
  ]);
};

export const useDocumentCarouselStyles = makeStyles({
  row: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: '0 1 auto',
    flexDirection: 'row',
    flexWrap: 'wrap',
    '@media (max-width: 960px)': {
      flexDirection: 'column',
    },
  },
  switch: {
    //The fluent-ui v9 switch is a bit misaligned to our existing components so needs a nudge up a few pixels.
    marginTop: '-6px',
    //The fluent-ui v9 switch is shorter than the dropdown components, so to avoid content "wriggling" when it's toggled, set the height to match.
    height: '40px',
  },
});

export const getDocumentCarouselReportParameters = ({
  getValues,
  reprint,
}: {
  getValues: UseFormGetValues<IDocumentValues>;
  reprint: boolean;
}): IReportParametersInput => {
  const documentValues = getValues();
  const { to, cc, from, subject, message } = documentValues.email;
  const {
    overrideTemplate,
    template,
    overrideOutput,
    outputType,
    printerId,
    printIndividually,
    printContiguous,
    resetToStart,
  } = documentValues;

  return {
    emailToList: to.map((entityEmail) => entityEmail.email).join(','),
    emailCcList: cc.map((entityEmail) => entityEmail.email).join(','),
    emailSubject: subject,
    emailFrom: from?.email,
    emailBody: message,
    overrideTemplate: overrideTemplate,
    template: normalizeVariables(template),
    overrideOutput: overrideOutput,
    outputType: outputType === 'PDF_ZIP' ? ReportOutputType.Pdf : outputType,
    printerId: printerId,
    printIndividually: printIndividually,
    printContiguous: printContiguous,
    jobType: reprint ? JobType.Reprint : JobType.New,
    resetToStart: resetToStart,
  };
};

const useBadgeStyles = makeStyles({
  badgeInfo: {
    backgroundColor: p4Tokens.statusFlagInfo,
  },
});

export const useSalesOrderDeliveryRecipient = (singlePrintNode: Maybe<ISalesOrder>): IEntityEmail => {
  const styles = useBadgeStyles();
  if (!singlePrintNode || !singlePrintNode.deliveryEmail) return undefined;

  return {
    id: 'delivery',
    email: singlePrintNode.deliveryEmail,
    name: singlePrintNode.customer?.name ?? 'Unknown',
    suffix: (
      <Badge shape={'rounded'} className={styles.badgeInfo}>
        Delivery
      </Badge>
    ),
  };
};
